import { useState } from "react";
import { Accordion } from "@base-ui/react/accordion";
import { ArrowRight, ChevronDown, Download, Play } from "lucide-react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import { InlineAlert } from "@/components/admin/feedback";
import { Button, buttonVariants } from "@/components/ui/button";
import { helpGuides, productSetupGuide, type HelpGuide } from "@/lib/getting-started";
import { useTimezone } from "@/lib/timezone";
import { can, type AdminRoleInput } from "@/lib/permissions";
import { useCurrentIdentityQuery } from "@/lib/queries";
import { adminApi, apiErrorMessage } from "@/lib/api";

export function downloadIntegrationGuideBundle(bundle: Blob) {
  const url = URL.createObjectURL(bundle);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "tb-license-platform-integration-guide.zip";
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    setTimeout(() => {
      anchor.remove();
      URL.revokeObjectURL(url);
    }, 0);
  }
}

export async function exportIntegrationGuideBundle() {
  downloadIntegrationGuideBundle(await adminApi.exportIntegrationGuide());
}

const guideTriggerClassName = "group flex min-h-14 w-full items-center justify-between gap-4 rounded-sm px-2 py-4 text-left text-ink hover:bg-panel focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus";

function Guide({ guide, roles }: { guide: HelpGuide; roles: AdminRoleInput }) {
  const { t } = useTranslation();
  const { timezoneAbbr } = useTimezone();
  const key = `help.guides.${guide.id}`;
  const allowed = can(roles, guide.permission);

  return (
    <Accordion.Item value={guide.id} className="border-b border-line">
      <Accordion.Header render={<h3 />}>
        <Accordion.Trigger className={guideTriggerClassName}>
          <span className="min-w-0">
            <span className="block text-base font-medium">{t(`${key}.title`)}</span>
            <span className="mt-1 block text-sm font-normal leading-6 text-muted">{t(`${key}.summary`)}</span>
          </span>
          <ChevronDown className="mr-1 size-4 shrink-0 text-muted group-data-panel-open:rotate-180" aria-hidden="true" />
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Panel className="pb-6 pt-3">
        <div className="flex max-w-[72ch] flex-col gap-5 text-sm leading-6">
          <div>
            <h4 className="font-medium text-ink">{t("help.before")}</h4>
            <p className="mt-1 text-muted">{t(`${key}.before`)}</p>
          </div>
          <ol className="list-decimal space-y-4 pl-8 marker:font-mono marker:text-muted">
            {guide.steps.map((step) => (
              <li key={step.id} className="pl-1">
                <h4 className="font-medium text-ink">{t(`${key}.steps.${step.id}.title`)}</h4>
                <p className="text-muted">{t(`${key}.steps.${step.id}.body`, { tz: timezoneAbbr })}</p>
                {step.href && (
                  <Link to={step.href} className="inline-flex min-h-9 items-center gap-1 rounded-sm text-ink underline decoration-muted underline-offset-4 hover:text-ink focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus pointer-coarse:min-h-11">
                    {t(`${key}.steps.${step.id}.link`)}
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                )}
              </li>
            ))}
          </ol>
          <div>
            <h4 className="font-medium text-ink">{t("help.outcome")}</h4>
            <p className="mt-1 text-muted">{t(`${key}.outcome`)}</p>
          </div>
          <div className="rounded-sm bg-panel px-4 py-3">
            <h4 className="font-medium text-ink">{t("help.note")}</h4>
            <p className="mt-1 text-muted">{t(`${key}.note`)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {allowed ? (
              <Link to={guide.href} className={buttonVariants({ variant: "primary" })}>
                {t(`${key}.action`)}
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            ) : (
              <Button variant="primary" disabled>{t(`${key}.action`)}</Button>
            )}
            {guide.tourHref && allowed && (
              <Link to={guide.tourHref} className={buttonVariants({ variant: "ghost" })}>
                <Play className="size-3.5" aria-hidden="true" />
                {t("help.startGuidedTour")}
              </Link>
            )}
          </div>
          {!allowed && <p className="text-muted">{t("help.permission")}</p>}
        </div>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

export function HelpPage() {
  const { t } = useTranslation();
  const identity = useCurrentIdentityQuery();
  const [isExportingIntegrationGuide, setIsExportingIntegrationGuide] = useState(false);
  const [integrationGuideExportError, setIntegrationGuideExportError] = useState<string | null>(null);
  const canExportIntegrationGuide = can(identity.data?.roles, "exportIntegrationGuide");
  const exportIntegrationGuide = async () => {
    if (!canExportIntegrationGuide || isExportingIntegrationGuide) return;
    setIsExportingIntegrationGuide(true);
    setIntegrationGuideExportError(null);
    try {
      await exportIntegrationGuideBundle();
    } catch (error) {
      setIntegrationGuideExportError(apiErrorMessage(error));
    } finally {
      setIsExportingIntegrationGuide(false);
    }
  };

  return (
    <PageFrame size="fluid" scrollable>
      <PageHeader title={t("help.title")} />
      <div className="flex w-full max-w-4xl flex-col gap-8 pb-6">
        <section aria-labelledby="help-tasks-heading">
          <h2 id="help-tasks-heading" className="mb-2 text-lg font-medium text-ink">{t("help.description")}</h2>
          <Accordion.Root>
            {helpGuides.map((guide) => <Guide key={guide.id} guide={guide} roles={identity.data?.roles} />)}
          </Accordion.Root>
        </section>

        <section aria-labelledby="help-resources-heading">
          <h2 id="help-resources-heading" className="mb-2 text-lg font-medium text-ink">{t("help.resources")}</h2>
          <Accordion.Root>
            <Guide guide={productSetupGuide} roles={identity.data?.roles} />
            <Accordion.Item value="glossary" className="border-b border-line">
              <Accordion.Header render={<h3 />}>
                <Accordion.Trigger className={guideTriggerClassName}>
                  <span className="text-base font-medium">{t("help.glossary")}</span>
                  <ChevronDown className="mr-1 size-4 shrink-0 text-muted group-data-panel-open:rotate-180" aria-hidden="true" />
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Panel className="pb-5">
                <dl className="grid max-w-[72ch] gap-4 text-sm leading-6 sm:grid-cols-2">
                  {["license", "productKey", "entitlement", "activation"].map((concept) => (
                    <div key={concept}>
                      <dt className="font-medium text-ink">{t(`help.concepts.${concept}`)}</dt>
                      <dd className="text-muted">{t(`help.concepts.${concept}Desc`)}</dd>
                    </div>
                  ))}
                </dl>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion.Root>
          <div className="flex flex-col items-start gap-3 pt-5" aria-labelledby="integration-guide-heading">
            <div className="max-w-[72ch]">
              <h3 id="integration-guide-heading" className="text-base font-medium text-ink">{t("help.integrationGuide.title")}</h3>
              <p className="mt-1 text-sm leading-6 text-muted">{t("help.integrationGuide.description")}</p>
            </div>
            <Button
              variant="outline"
              disabled={!canExportIntegrationGuide || isExportingIntegrationGuide}
              aria-busy={isExportingIntegrationGuide || undefined}
              aria-describedby={!canExportIntegrationGuide ? "integration-guide-permission" : undefined}
              onClick={exportIntegrationGuide}
            >
              <Download className="size-4" aria-hidden="true" />
              {isExportingIntegrationGuide ? t("help.integrationGuide.exporting") : t("help.integrationGuide.export")}
            </Button>
            {!canExportIntegrationGuide && <p id="integration-guide-permission" className="text-sm text-muted">{t("help.integrationGuide.permission")}</p>}
            {integrationGuideExportError && (
              <InlineAlert title={t("help.integrationGuide.exportErrorTitle")} message={integrationGuideExportError} />
            )}
            <p className="max-w-[72ch] text-xs leading-5 text-muted">{t("help.integrationGuide.safety")}</p>
          </div>
        </section>
      </div>
    </PageFrame>
  );
}
